import { formatCryptoUsd } from '../utils/cryptoDecimal';
import { Doughnut } from 'react-chartjs-2';
import Chart, { ArcElement } from 'chart.js/auto';
import { fetchPolygonSnapshotPrice } from '../composables/fetchPolygon';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useExchangeRates } from '../context/ExchangeRateContext';

Chart.register(ArcElement);

interface PortfolioSingleChartBoxProps {
  portfolios: any; // Single portfolio object
  currentPrices: { [symbol: string]: number };
  setIsConfigPopupOpen: (isOpen: boolean) => void; // เพิ่ม props สำหรับการตั้งค่า popup
  isAdminPort?: boolean; // เพิ่ม props เพื่อตรวจสอบว่าเป็นพอร์ตของแอดมินหรือไม่
  currency?: string;
  convertCurrency?: (value: number, currency: string) => number;
  isShay?: boolean; // เพิ่ม props เพื่อตรวจสอบว่าเป็นพอร์ตของแอดมินหรือไม่
  isCrypto?: boolean; // พอร์ตคริปโต: ชื่อเหรียญแบบ BTC และยอดเล็กมากไม่ปัดเป็น 0.00

}

const COLORS = ['#3b82f6', '#22c55e', '#f59e42', '#e11d48', '#a855f7'];
// โทนสีหลัก 10 โทน (5 สีเดิม + 5 สีใหม่) ในรูปแบบองศา Hue บนวงจรสี
const BASE_HUES = [
  217, // Blue (จาก #3b82f6)
  142, // Green (จาก #22c55e)
  31,  // Orange (จาก #f59e42)
  347, // Red (จาก #e11d48)
  271, // Purple (จาก #a855f7)
  // 173, // Teal (สีน้ำทะเล)
  // 330, // Pink (สีชมพู)
  // 45,  // Yellow (สีเหลืองตุ่น)
  // 239, // Indigo (สีคราม)
  // 190, // Cyan (สีฟ้าอ่อน)
];

// ความสว่าง 6 ระดับ (ค่าน้อย = เข้มมาก, ค่ามาก = อ่อนมาก)
const LIGHTNESS_LEVELS = [ 50, 55,60, 65,70];

const PortfolioSingleChartBox: React.FC<PortfolioSingleChartBoxProps> = ({
  portfolios,
  setIsConfigPopupOpen,
  isAdminPort,
  currency = 'USD', // รับค่า currency
  convertCurrency,
  currentPrices,
  isShay = false,
  isCrypto = false
}) => {
  const money = (v: number) => (isCrypto ? formatCryptoUsd(v) : v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  const labelOf = (s: any) => (isCrypto ? (s.displaySymbol || s.stockSymbol) : s.stockSymbol);
  const stocks = portfolios.stocks || [];
  // const [currentPrices, setCurrentPrices] = useState<{ [symbol: string]: number }>({});
  // const [loading, setLoading] = useState(true); // เพิ่มสถานะ loading
  const { exchangeRates } = useExchangeRates(); // ใช้ exchangeRates จาก Context
  const exchangeRate = currency === 'THB' ? exchangeRates['THB'] || 1 : 1;

  // คำนวณมูลค่าหุ้นในพอร์ต
  const stockValues = stocks.map((stock: any) => ({
    stockSymbol: stock.stockSymbol,
    label: labelOf(stock),
    quantity: stock.quantity,
    currentValue: stock.quantity * (currentPrices[stock.stockSymbol] ?? 0) * exchangeRate, // แปลงค่าเงิน
  }));

  const totalStockValue = stockValues.reduce((sum: number, stock: any) => sum + stock.currentValue, 0);
  const totalCost = stocks.reduce((sum: number, stock: { quantity?: number; avgBuyPrice?: number }) => {
    return sum + (stock.quantity ?? 0) * (stock.avgBuyPrice ?? 0) * exchangeRate; // แปลงค่าเงิน
  }, 0);

  const overallProfit = totalStockValue - totalCost;
  const overallProfitPercent = totalCost > 0 ? (overallProfit / totalCost) * 100 : 0; // เปอร์เซ็นต์กำไร/ขาดทุน
  const totalTarget = stocks.reduce((sum: number, stock: any) => sum + (stock.expectedRatio || 0), 0);
  // ฟังก์ชันสร้างสี โดยไล่ไปตาม 10 สีหลักให้ครบก่อน แล้วค่อยเปลี่ยนระดับความเข้ม-อ่อน
  const generateMultiShadeColor = (index: number) => {
    const hue = BASE_HUES[index % BASE_HUES.length];
    // เปลี่ยนระดับความเข้มทุกๆ 10 หุ้น (เมื่อวนสีหลักครบ)
    const lightnessIndex = Math.floor(index / BASE_HUES.length) % LIGHTNESS_LEVELS.length;
    const lightness = LIGHTNESS_LEVELS[lightnessIndex];
    // Saturation 80% ให้สีดูสดใสกำลังดี
    return `hsl(${hue}, 80%, ${lightness}%)`;
  };

  const dynamicColors = stockValues.map((_: any, idx: number) => generateMultiShadeColor(idx));

  const data = {
    labels: stocks.map((stock: any) => labelOf(stock)),
    datasets: [
      {
        data: stockValues.map((stock: any) => stock.currentValue),
        backgroundColor: dynamicColors,
        borderWidth: 0,
      },
    ],
  };

  const handleOpenConfigPopup = () => {
    if (setIsConfigPopupOpen) {
      setIsConfigPopupOpen(true); // เรียกใช้งานฟังก์ชัน toggle จาก component แม่
    } else {
      toast.error("setIsConfigPopupOpen is not defined");
    }
  };

  return (
    <div
      className="rounded-xl pt-16 shadow-lg p-4 md:h-[700px] flex flex-col items-center justify-start md:sticky md:top-0"
      style={{
        background: 'var(--component-bg-color)',
        overflowY: 'auto',
      }}
    >
      <div className="w-full flex flex-col items-center">
        {/* ปรับขนาดวงกลมใน Mobile ให้เล็กลง (w-64 h-64) แต่ Desktop เท่าเดิม (w-96 h-96) */}
        <div className="w-64 h-64 md:w-80 md:h-80 relative">
          <Doughnut
            data={data}
            options={{
              cutout: '76%',
              plugins: { legend: { display: false } },
            }}
          />
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none px-4 text-center">
            <span className="text-lg md:text-2xl font-bold truncate w-full" style={{ color: 'var(--text-color)' }}>
              {portfolios.name}
            </span>
            <span className="text-xl md:text-3xl font-bold" style={{ color: 'var(--text-color)' }}>
              {currency === 'THB' ? '฿' : '$'}
              {money(totalStockValue)}
            </span>
            <span className={`text-base md:text-lg font-semibold ${overallProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {overallProfit >= 0 ? '+' : ''}
              {currency === 'THB' ? '฿' : '$'}
              {money(overallProfit)}
            </span>
            <span className={`text-xs md:text-sm ${overallProfitPercent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              ({overallProfitPercent >= 0 ? '+' : ''}{overallProfitPercent.toFixed(2)}%)
            </span>
          </div>
        </div>

        {/* Legend & Target Section */}
        <div className="w-full mt-6">
          <div className="flex justify-center gap-2 md:gap-4 flex-wrap mb-4">
            {stockValues.map((stock: any, idx: any) => (
              <div key={stock.stockSymbol || idx} className="flex items-center gap-2 bg-black/20 px-2 py-1 rounded">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: dynamicColors[idx] }}></span>
                <span className="text-xs md:text-sm" style={{ color: 'var(--text-color)' }}>{stock.label}</span>
              </div>
            ))}
          </div>

          <div className="flex flex-col md:flex-row justify-between items-center border-t border-gray-700 pt-4 gap-3">

            {!isShay && (
              <div className="text-center md:text-left">
                <span className="text-gray-400 text-xs md:text-sm" style={{ color: 'var(--secondary-text-color)' }}>
                  เป้าหมายรวมในพอร์ตนี้
                </span>
                <span className="text-base md:text-lg font-bold ml-2" style={{ color: 'var(--yellow-text-color)' }}>
                  {totalTarget.toFixed(0)}% <span style={{ color: 'var(--text-color)' }}>/ 100%</span>
                </span>
              </div>
            )}


            {/* {!isAdminPort && (
            <button
              onClick={handleOpenConfigPopup}
              className="w-full md:w-auto bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded font-bold transition-all active:scale-95"
            >
              แก้ไขสัดส่วน
            </button>
          )} */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioSingleChartBox;